// UsuariosView — lista, cria e edita usuários com paridade da admin legacy.
//
// Campos suportados:
//   - Nome, email, apelido
//   - Tags (namespace:valor)
//   - Flags (canExportPdf)
//   - mustChangePassword (force change no próximo login)
//   - Suspended (suspendedAt)
//   - Reset senha
//   - Platform admin toggle
//   - Vincular a Company com role (admin_company/teacher/student)
//   - Remover vínculo de Company
//
// Não inclui ainda (próxima passada): Passkeys, Entitlements matrix, Grupos.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Ban,
  Building2,
  CheckCircle2,
  Eye,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Save,
  Search,
  Send,
  Shield,
  Tag,
  Trash2,
  Users,
  X,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, call, type BankListItem, type CompanyMin, type UserRow } from "./api";
import { BankGrantsForUser } from "./BankGrantsSection";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const userList = signal<UserRow[] | null>(null);
const companies = signal<CompanyMin[]>([]);
const allBanks = signal<BankListItem[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterRole = signal<"all" | "admin" | "teacher" | "student" | "suspended">("all");
const editing = signal<UserRow | null>(null);
const creating = signal<boolean>(false);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    // Carrega users + banks em paralelo. Banks é leve aqui (sem questoes
    // serializados, só metadado) e necessário pro picker de grants direto.
    const [ures, bres] = await Promise.all([api.listUsers(), api.listBanks()]);
    userList.value = ures.users;
    companies.value = ures.companies;
    allBanks.value = bres.banks;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function UsuariosView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") return <UsersLoading />;
  if (phase.value === "error") return <UsersError />;
  return (
    <>
      <UsersReady />
      {editing.value && <EditUserModal user={editing.value} />}
      {creating.value && <CreateUserModal />}
    </>
  );
}

function UsersLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-12 rounded-md" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-md" />
      ))}
    </div>
  );
}

function UsersError() {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Falha ao listar usuários</h3>
          {errMsg.value && (
            <p class="text-xs text-muted-foreground">{errMsg.value}</p>
          )}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersReady() {
  const rows = userList.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fr = filterRole.value;
  const visible = useMemo(() => {
    return rows.filter((u) => {
      if (fr === "admin" && !u.platformRoles.includes("admin")) return false;
      if (fr === "teacher" && !u.companies.some((c) => c.role === "teacher"))
        return false;
      if (fr === "student" && !u.companies.some((c) => c.role === "student"))
        return false;
      if (fr === "suspended" && !u.suspendedAt) return false;
      if (q) {
        const hay = `${u.email} ${u.name} ${u.apelido ?? ""} ${u.tags.join(" ")} ${u.companies.map((c) => c.companyName).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fr]);

  const counts = useMemo(() => {
    let admin = 0,
      teacher = 0,
      student = 0,
      suspended = 0;
    for (const u of rows) {
      if (u.platformRoles.includes("admin")) admin++;
      if (u.companies.some((c) => c.role === "teacher")) teacher++;
      if (u.companies.some((c) => c.role === "student")) student++;
      if (u.suspendedAt) suspended++;
    }
    return { admin, teacher, student, suspended, total: rows.length };
  }, [rows]);

  return (
    <div class="space-y-4">
      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar email, nome, apelido, tag, company…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <RolePill
          label="Todos"
          count={counts.total}
          active={filterRole.value === "all"}
          onClick={() => (filterRole.value = "all")}
        />
        <RolePill
          label="Admin"
          count={counts.admin}
          active={filterRole.value === "admin"}
          onClick={() => (filterRole.value = "admin")}
        />
        <RolePill
          label="Prof"
          count={counts.teacher}
          active={filterRole.value === "teacher"}
          onClick={() => (filterRole.value = "teacher")}
        />
        <RolePill
          label="Aluno"
          count={counts.student}
          active={filterRole.value === "student"}
          onClick={() => (filterRole.value = "student")}
        />
        <RolePill
          label="Suspenso"
          count={counts.suspended}
          active={filterRole.value === "suspended"}
          onClick={() => (filterRole.value = "suspended")}
        />
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Novo
        </button>
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {rows.length} usuário{rows.length !== 1 ? "s" : ""}
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum usuário encontrado nesse filtro.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Usuário</th>
                <th class="px-3 py-2">Roles</th>
                <th class="px-3 py-2">Companies</th>
                <th class="px-3 py-2">Tags</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <UserListRow key={u.id} user={u} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RolePill({
  label,
  count,
  active,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
      <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums opacity-80">
        {count}
      </span>
    </button>
  );
}

function UserListRow({ user }: { user: UserRow }) {
  return (
    <tr
      class={`border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30 ${
        user.suspendedAt ? "opacity-60" : ""
      }`}
    >
      <td class="px-3 py-2">
        <div class="font-semibold">
          {user.name}
          {user.apelido && (
            <span class="ml-1.5 text-[11px] font-normal text-muted-foreground">
              ({user.apelido})
            </span>
          )}
        </div>
        <div class="font-mono text-[11px] text-muted-foreground">{user.email}</div>
      </td>
      <td class="px-3 py-2">
        {user.platformRoles.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="flex flex-wrap gap-1">
            {user.platformRoles.map((r) => (
              <span
                key={r}
                class="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-fuchsia-500/15 to-violet-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 ring-1 ring-violet-500/30 dark:text-violet-300"
              >
                <Shield width="0.9em" height="0.9em" />
                {r}
              </span>
            ))}
          </div>
        )}
      </td>
      <td class="px-3 py-2">
        {user.companies.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="space-y-0.5">
            {user.companies.map((c) => (
              <div
                key={`${c.companyId}-${c.role}`}
                class="flex items-center gap-1 text-[11px]"
              >
                <Building2 width="0.85em" height="0.85em" className="text-muted-foreground" />
                <span class="font-medium">{c.companyName}</span>
                <span
                  class={`rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider ${
                    c.role === "admin_company"
                      ? "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                      : c.role === "teacher"
                        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        : "bg-sky-500/15 text-sky-700 dark:text-sky-300"
                  }`}
                >
                  {c.role === "admin_company" ? "Admin" : c.role === "teacher" ? "Prof" : "Aluno"}
                </span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td class="px-3 py-2">
        {user.tags.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="flex flex-wrap gap-1">
            {user.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                class="inline-flex items-center gap-0.5 rounded bg-accent px-1.5 py-0.5 text-[10px] font-mono"
              >
                <Tag width="0.8em" height="0.8em" />
                {t}
              </span>
            ))}
            {user.tags.length > 3 && (
              <span class="text-[10px] text-muted-foreground">
                +{user.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </td>
      <td class="px-3 py-2">
        <div class="flex flex-wrap gap-1">
          {user.suspendedAt && (
            <span
              title="Suspenso"
              class="inline-flex items-center gap-0.5 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400"
            >
              <Ban width="0.85em" height="0.85em" />
              Suspenso
            </span>
          )}
          {user.mustChangePassword && (
            <span
              title="Vai trocar senha no próximo login"
              class="inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300"
            >
              <KeyRound width="0.85em" height="0.85em" />
              Trocar pwd
            </span>
          )}
          {!user.suspendedAt && !user.mustChangePassword && (
            <span class="text-[10px] italic text-muted-foreground/70">ok</span>
          )}
        </div>
      </td>
      <td class="px-3 py-2 text-right">
        <div class="inline-flex gap-1">
          <ImpersonateButton user={user} />
          <ResetPasswordButton user={user} />
          <AssignPlanButton user={user} />
          <button
            type="button"
            onClick={() => (editing.value = user)}
            title="Editar"
            aria-label="Editar"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            <Pencil width="0.95em" height="0.95em" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Botão Impersonate: admin entra como o user em modo READ-ONLY.
// [[project-admin-impersonate-readonly]]
function ImpersonateButton({ user }: { user: UserRow }) {
  const [busy, setBusy] = useState(false);
  async function doImpersonate() {
    const ok = await openConfirm({
      title: "Entrar como este usuário?",
      message: `Vou abrir o portal como ${user.name} (${user.email}) em MODO LEITURA. Você verá tudo que ele vê, mas não poderá enviar respostas, mensagens, anotações ou qualquer mudança. Use só pra debug.`,
      confirmLabel: "Entrar (read-only)"
    });
    if (!ok) return;
    setBusy(true);
    try {
      await call("/api/manager/impersonate", {
        method: "POST",
        body: JSON.stringify({ userId: user.id })
      });
      // Hard reload pra puxar contexts/profiles do user impersonado.
      window.location.href = "/student/";
    } catch (e) {
      void openAlert({
        title: "Não consegui iniciar Impersonate",
        message: friendlyError(e)
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={doImpersonate}
      disabled={busy}
      title="Entrar como este usuário (read-only)"
      aria-label="Impersonate"
      class="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-700 hover:bg-amber-500/20 dark:text-amber-300 disabled:opacity-50"
    >
      <Eye width="0.95em" height="0.95em" />
    </button>
  );
}

// Botão de quick action: gera nova senha temporária e dispara email.
function ResetPasswordButton({ user }: { user: UserRow }) {
  const [busy, setBusy] = useState(false);
  async function doReset() {
    const ok = await openConfirm({
      title: "Resetar senha por email?",
      message: `Vou gerar uma senha temporária pra ${user.name} (${user.email}) e enviar por email. O usuário será forçado a trocar a senha no próximo login.`,
      confirmLabel: "Resetar e enviar"
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.resetUserPassword(user.id);
      if (r.emailSent) {
        await openAlert({
          title: "Senha resetada",
          message: `Email enviado pra ${user.email} com a nova senha temporária.`
        });
      } else {
        await openAlert({
          title: "Senha resetada (sem email)",
          message: `A senha foi resetada, mas o email não pôde ser enviado: ${r.emailReason ?? "verifique a configuração do Resend"}.`
        });
      }
    } catch (e) {
      await openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={doReset}
      disabled={busy}
      title="Resetar senha e enviar por email"
      aria-label="Resetar senha"
      class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-600 disabled:opacity-50"
    >
      {busy ? (
        <Loader2 width="0.95em" height="0.95em" className="animate-spin" />
      ) : (
        <KeyRound width="0.95em" height="0.95em" />
      )}
    </button>
  );
}

// Botão pra atribuir um platform plan a esse usuário (mesmo restrito).
// Carrega planos sob demanda no clique pra evitar fetch desnecessário.
function AssignPlanButton({ user }: { user: UserRow }) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<{
    id: string;
    slug: string;
    name: string;
    monthlyCents: number;
    isPublic: boolean;
    isActive: boolean;
  }[] | null>(null);
  const [loading, setLoading] = useState(false);

  async function openModal() {
    setOpen(true);
    if (plans) return;
    setLoading(true);
    try {
      const r = await api.listPlatformPlans();
      setPlans(
        r.plans
          .filter((p) => p.isActive)
          .map((p) => ({
            id: p.id,
            slug: p.slug,
            name: p.name,
            monthlyCents: p.monthlyCents,
            isPublic: p.isPublic,
            isActive: p.isActive
          }))
      );
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setLoading(false);
    }
  }

  async function pickPlan(slug: string, planName: string) {
    try {
      await api.assignPlan({ userId: user.id, slug });
      setOpen(false);
      void openAlert({
        title: "Plano atribuído",
        message: `${user.name} agora está no plano ${planName}.`
      });
    } catch (e) {
      void openAlert({
        title: "Erro ao atribuir",
        message: friendlyError(e)
      });
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title="Atribuir plano da plataforma"
        aria-label="Atribuir plano da plataforma"
        class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
      >
        Plano
      </button>
      {open && (
        <div
          class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
          /* Modal admin não fecha por click no backdrop. [[feedback-modal-no-click-outside-close]] */
        >
          <div class="w-full max-w-md rounded-md border border-border bg-card p-5 shadow-2xl">
            <h3 class="mb-1 text-base font-bold">Atribuir plano</h3>
            <p class="mb-3 text-xs text-muted-foreground">
              {user.name} · {user.email}
            </p>
            {loading || !plans ? (
              <p class="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <ul class="space-y-1">
                {plans.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => pickPlan(p.slug, p.name)}
                      class="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary hover:bg-accent"
                    >
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-medium">{p.name}</span>
                          {!p.isPublic && (
                            <span class="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:text-amber-300">
                              restrito
                            </span>
                          )}
                        </div>
                        <code class="text-[10px] text-muted-foreground">
                          {p.slug}
                        </code>
                      </div>
                      <div class="text-right text-xs tabular-nums">
                        {p.monthlyCents === 0
                          ? "Grátis"
                          : (p.monthlyCents / 100)
                              .toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL"
                              }) + "/mês"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div class="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =====================
// Form fields compartilhados
// =====================

function FormSection({ title, children }: { title: string; children: any }) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function LabeledInput({
  label,
  value,
  type = "text",
  placeholder,
  maxLength,
  onChange
}: {
  label: string;
  value: string;
  type?: string;
  placeholder?: string;
  maxLength?: number;
  onChange: (v: string) => void;
}) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
      />
    </label>
  );
}

function TagsInput({
  value,
  onChange
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  function add() {
    const t = draft.trim().toLowerCase();
    if (!t || value.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
  }
  return (
    <div>
      <div class="flex gap-2">
        <input
          type="text"
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="namespace:valor"
          class="flex-1 rounded-md border border-border bg-background px-3 py-1 text-xs outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={add}
          class="rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
        >
          + Tag
        </button>
      </div>
      {value.length > 0 && (
        <ul class="mt-2 flex flex-wrap gap-1.5">
          {value.map((t) => (
            <li
              key={t}
              class="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[11px]"
            >
              <Tag width="0.85em" height="0.85em" />
              <span class="font-mono">{t}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== t))}
                title="Remover tag"
                aria-label="Remover tag"
                class="text-muted-foreground hover:text-destructive"
              >
                <XCircle width="11" height="11" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompanyMembershipPicker({
  companyId,
  role,
  onChange
}: {
  companyId: string | "";
  role: "" | "admin_company" | "teacher" | "student";
  onChange: (companyId: string, role: "" | "admin_company" | "teacher" | "student") => void;
}) {
  return (
    <div class="grid grid-cols-2 gap-2">
      <select
        value={companyId}
        onChange={(e) => onChange((e.target as HTMLSelectElement).value, role)}
        title="Company"
        aria-label="Company"
        class="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
      >
        <option value="">— Sem vínculo —</option>
        {companies.value.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        value={role}
        onChange={(e) =>
          onChange(
            companyId,
            (e.target as HTMLSelectElement).value as "" | "admin_company" | "teacher" | "student"
          )
        }
        disabled={!companyId}
        title="Role na Company"
        aria-label="Role na Company"
        class="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-50"
      >
        <option value="">— Role —</option>
        <option value="student">Aluno</option>
        <option value="teacher">Professor</option>
        <option value="admin_company">Admin da Company</option>
      </select>
    </div>
  );
}

// =====================
// Create modal
// =====================

function CreateUserModal() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [apelido, setApelido] = useState("");
  const [password, setPassword] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [canExportPdf, setCanExportPdf] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(true);
  // Default OFF: quando admin define a senha direto e provavelmente já
  // combinou com a pessoa, NÃO enviar email automaticamente. Admin liga
  // este switch explicitamente quando quer que a plataforma envie.
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [companyId, setCompanyId] = useState<string>("");
  const [role, setRole] = useState<"" | "admin_company" | "teacher" | "student">("");
  const [submitting, setSubmitting] = useState(false);

  const validEmail = email.includes("@");
  const validPwd = password.length >= 8 && password.length <= 64;
  const valid = validEmail && name.trim().length > 0 && validPwd;

  async function handleCreate() {
    if (!valid) return;
    setSubmitting(true);
    try {
      const r = await api.createUser({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        apelido: apelido.trim() || null,
        password,
        tags,
        flags: { canExportPdf },
        mustChangePassword,
        platformAdmin,
        sendWelcomeEmail,
        companyMembership:
          companyId && role
            ? { companyId, role: role as "admin_company" | "teacher" | "student" }
            : undefined
      });
      await load();
      creating.value = false;
      // Avisa o admin se pediu pra enviar email mas falhou no envio.
      if (sendWelcomeEmail && r.emailSent === false) {
        void openAlert({
          title: "Usuário criado (sem email)",
          message: `O cadastro foi feito, mas o email não pôde ser enviado: ${r.emailReason ?? "verifique a configuração do Resend"}.`
        });
      }
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao criar: ${err.message}` : "Falha ao criar."
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Novo usuário" onClose={() => (creating.value = false)} icon={Plus}>
      <FormSection title="Identidade">
        <div class="space-y-2">
          <LabeledInput label="Nome completo" value={name} maxLength={80} onChange={setName} />
          <LabeledInput
            label="Apelido (opcional)"
            value={apelido}
            maxLength={40}
            onChange={setApelido}
          />
          <LabeledInput
            label="Email"
            value={email}
            type="email"
            placeholder="ana@exemplo.com"
            onChange={setEmail}
          />
          <LabeledInput
            label="Senha temporária"
            value={password}
            placeholder="8-64 caracteres"
            onChange={setPassword}
          />
          <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs">
            <Switch
              checked={mustChangePassword}
              onCheckedChange={(v) => setMustChangePassword(v)}
              class="size-3.5 accent-primary"
            />
            <span>Forçar troca de senha no próximo login</span>
          </label>
          <label class="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs">
            <Switch
              checked={sendWelcomeEmail}
              onCheckedChange={(v) => setSendWelcomeEmail(v)}
            />
            <Mail width="1em" height="1em" className="text-primary" />
            <span>
              Enviar email de boas-vindas com a senha temporária{" "}
              <span class="text-muted-foreground">
                (deixe desligado se você mesmo for avisar a pessoa)
              </span>
            </span>
          </label>
        </div>
      </FormSection>

      <FormSection title="Vínculo com Company (opcional)">
        <CompanyMembershipPicker
          companyId={companyId}
          role={role}
          onChange={(c, r) => {
            setCompanyId(c);
            setRole(r);
          }}
        />
      </FormSection>

      <FormSection title="Tags">
        <TagsInput value={tags} onChange={setTags} />
      </FormSection>

      <FormSection title="Permissões">
        <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs">
          <Switch
            checked={canExportPdf}
            onCheckedChange={(v) => setCanExportPdf(v)}
          />
          <span>Pode exportar PDF</span>
        </label>
        <label class="flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 text-xs">
          <Switch
            checked={platformAdmin}
            onCheckedChange={(v) => setPlatformAdmin(v)}
          />
          <Shield width="1em" height="1em" className="text-violet-600 dark:text-violet-400" />
          <span>Admin de plataforma</span>
        </label>
      </FormSection>

      <ModalActions
        onCancel={() => (creating.value = false)}
        onSubmit={handleCreate}
        submitting={submitting}
        valid={valid}
        submitIcon={CheckCircle2}
        submitLabel="Criar"
      />
    </Modal>
  );
}

// =====================
// Storage helpers (caderno quota) — espelha lib/storage-quota.ts do backend
// [[project-caderno-storage-quota]]
// =====================

const DEFAULT_QUOTA_MB = 100;

function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  const mb = n / (1024 * 1024);
  if (mb < 1024) return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function quotaPct(used: number, quota: number): number {
  if (quota <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, (used / quota) * 100);
}

function quotaTone(used: number, quota: number): string {
  const pct = quotaPct(used, quota);
  if (pct >= 90) return "bg-destructive";
  if (pct >= 70) return "bg-amber-500";
  return "bg-violet-500";
}

// =====================
// Edit modal
// =====================

function EditUserModal({ user }: { user: UserRow }) {
  const [name, setName] = useState(user.name);
  const [apelido, setApelido] = useState(user.apelido ?? "");
  const [email, setEmail] = useState(user.email);
  const [tags, setTags] = useState<string[]>(user.tags);
  const [canExportPdf, setCanExportPdf] = useState(!!user.flags.canExportPdf);
  const [mustChangePassword, setMustChangePassword] = useState(user.mustChangePassword);
  const [suspended, setSuspended] = useState(!!user.suspendedAt);
  const [platformAdmin, setPlatformAdmin] = useState(
    user.platformRoles.includes("admin")
  );
  const [newPassword, setNewPassword] = useState("");
  const [companyId, setCompanyId] = useState<string>("");
  const [role, setRole] = useState<"" | "admin_company" | "teacher" | "student">("");
  const [saving, setSaving] = useState(false);
  const wasAdmin = user.platformRoles.includes("admin");
  // Storage quota — null = usa default global (100 MB). String vazia no input
  // = manda null no save. [[project-caderno-storage-quota]]
  const [storageQuotaMb, setStorageQuotaMb] = useState<string>(
    user.storageQuotaBytes == null
      ? ""
      : String(Math.round(user.storageQuotaBytes / (1024 * 1024)))
  );

  async function handleSave() {
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {};
      if (name.trim() !== user.name) updates.name = name.trim();
      if (apelido.trim() !== (user.apelido ?? "")) {
        updates.apelido = apelido.trim() || null;
      }
      if (email.trim().toLowerCase() !== user.email) {
        updates.email = email.trim().toLowerCase();
      }
      if (JSON.stringify(tags) !== JSON.stringify(user.tags)) {
        updates.tags = tags;
      }
      if (canExportPdf !== !!user.flags.canExportPdf) {
        updates.flags = { ...user.flags, canExportPdf };
      }
      if (mustChangePassword !== user.mustChangePassword) {
        updates.mustChangePassword = mustChangePassword;
      }
      if (suspended !== !!user.suspendedAt) {
        updates.suspended = suspended;
      }
      if (platformAdmin !== wasAdmin) {
        updates.platformAdmin = platformAdmin;
      }
      if (newPassword.length >= 8) {
        updates.password = newPassword;
      }
      if (companyId && role) {
        updates.companyMembership = { companyId, role };
      }
      // Storage quota: input em MB → bytes. Vazio = null (volta pro default).
      const trimmedMb = storageQuotaMb.trim();
      const currentMb =
        user.storageQuotaBytes == null
          ? ""
          : String(Math.round(user.storageQuotaBytes / (1024 * 1024)));
      if (trimmedMb !== currentMb) {
        if (trimmedMb === "") {
          updates.storageQuotaBytes = null;
        } else {
          const parsed = parseInt(trimmedMb, 10);
          if (Number.isFinite(parsed) && parsed >= 0) {
            updates.storageQuotaBytes = parsed * 1024 * 1024;
          }
        }
      }
      if (Object.keys(updates).length === 0) {
        editing.value = null;
        return;
      }
      await api.patchUser(user.id, updates);
      await load();
      editing.value = null;
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar."
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveCompany(c: { companyId: string; role: string }) {
    if (!confirm(`Remover ${user.name} de ${c.role} nesta company?`)) return;
    try {
      await api.patchUser(user.id, {
        removeCompanyMembership: { companyId: c.companyId, role: c.role }
      });
      await load();
      editing.value = userList.value?.find((u) => u.id === user.id) ?? null;
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao remover" }) });
    }
  }

  return (
    <Modal title="Editar usuário" onClose={() => (editing.value = null)} icon={Users}>
      <FormSection title="Identidade">
        <div class="space-y-2">
          <LabeledInput label="Nome" value={name} maxLength={80} onChange={setName} />
          <LabeledInput
            label="Apelido"
            value={apelido}
            maxLength={40}
            onChange={setApelido}
          />
          <LabeledInput label="Email" value={email} type="email" onChange={setEmail} />
        </div>
      </FormSection>

      <FormSection title="Vínculos com Companies">
        {user.companies.length > 0 && (
          <ul class="space-y-1">
            {user.companies.map((c) => (
              <li
                key={`${c.companyId}-${c.role}`}
                class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs"
              >
                <Building2 width="1em" height="1em" className="text-muted-foreground" />
                <span class="flex-1 font-medium">{c.companyName}</span>
                <span class="rounded bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase">
                  {c.role === "admin_company"
                    ? "Admin"
                    : c.role === "teacher"
                      ? "Prof"
                      : "Aluno"}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveCompany(c)}
                  title="Remover vínculo"
                  aria-label="Remover vínculo"
                  class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 width="0.95em" height="0.95em" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div class="space-y-1">
          <p class="text-[10px] text-muted-foreground/70">
            Adicionar vínculo / trocar role:
          </p>
          <CompanyMembershipPicker
            companyId={companyId}
            role={role}
            onChange={(c, r) => {
              setCompanyId(c);
              setRole(r);
            }}
          />
        </div>
      </FormSection>

      <FormSection title="Tags">
        <TagsInput value={tags} onChange={setTags} />
      </FormSection>

      <BankGrantsForUser user={user} allBanks={allBanks.value} />

      <FormSection title="Senha">
        <LabeledInput
          label="Resetar senha (deixe vazio pra não mexer)"
          value={newPassword}
          placeholder="Nova senha temporária (8-64)"
          onChange={setNewPassword}
        />
        <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs">
          <Switch
            checked={mustChangePassword}
            onCheckedChange={(v) => setMustChangePassword(v)}
          />
          <KeyRound width="1em" height="1em" />
          <span>Forçar troca no próximo login</span>
        </label>
      </FormSection>

      <FormSection title="Permissões e status">
        <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs">
          <Switch
            checked={canExportPdf}
            onCheckedChange={(v) => setCanExportPdf(v)}
          />
          <span>Pode exportar PDF</span>
        </label>
        <label class="flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 text-xs">
          <Switch
            checked={platformAdmin}
            onCheckedChange={(v) => setPlatformAdmin(v)}
          />
          <Shield width="1em" height="1em" className="text-violet-600 dark:text-violet-400" />
          <span>Admin de plataforma</span>
        </label>
        <label class="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1.5 text-xs">
          <Switch
            checked={suspended}
            onCheckedChange={(v) => setSuspended(v)}
          />
          <Ban width="1em" height="1em" className="text-red-600 dark:text-red-400" />
          <span>Suspender usuário (bloqueia login)</span>
        </label>
      </FormSection>

      <FormSection title="Armazenamento (cadernos)">
        {/* [[project-caderno-storage-quota]] — uso agregado de todos os
            ai_study_documents do aluno + cota individual editável. Vazio
            = usa default global (100 MB). Plano-based vem depois. */}
        <div class="rounded-md border border-border bg-background/50 p-2.5 text-xs">
          <div class="mb-1.5 flex items-center justify-between gap-2">
            <span class="text-muted-foreground">Em uso agora</span>
            <span class="font-bold tabular-nums">
              {fmtBytes(user.storageUsedBytes)} /{" "}
              {fmtBytes(
                user.storageQuotaBytes ?? DEFAULT_QUOTA_MB * 1024 * 1024
              )}
            </span>
          </div>
          <div class="h-1.5 overflow-hidden rounded-full bg-border">
            <div
              class={`h-full transition-all ${
                quotaTone(
                  user.storageUsedBytes,
                  user.storageQuotaBytes ?? DEFAULT_QUOTA_MB * 1024 * 1024
                )
              }`}
              style={{
                width: `${Math.min(100, quotaPct(user.storageUsedBytes, user.storageQuotaBytes ?? DEFAULT_QUOTA_MB * 1024 * 1024))}%`
              }}
            />
          </div>
        </div>
        <div class="flex items-end gap-2">
          <LabeledInput
            label="Cota (MB) — vazio = default global (100 MB)"
            value={storageQuotaMb}
            placeholder="Ex: 500"
            type="text"
            onChange={(v) => setStorageQuotaMb(v.replace(/[^0-9]/g, ""))}
          />
        </div>
        <p class="text-[10px] text-muted-foreground/80">
          0 = bloqueia uploads novos. Quando o aluno tentar subir doc acima da
          cota, recebe erro 413 amigável com instrução de limpar ou pedir
          aumento. (Em breve: defaults por plano.)
        </p>
      </FormSection>

      <ModalActions
        onCancel={() => (editing.value = null)}
        onSubmit={handleSave}
        submitting={saving}
        valid={name.trim().length > 0 && email.includes("@")}
        submitIcon={Save}
        submitLabel="Salvar"
      />
    </Modal>
  );
}

// =====================
// Modal chrome reusável
// =====================

function Modal({
  title,
  icon: Icon,
  children,
  onClose
}: {
  title: string;
  icon: typeof Users;
  children: any;
  onClose: () => void;
}) {
  // ESC fecha (pattern de teclado). Click no backdrop NÃO fecha — feedback
  // do user 2026-05-19: criar/editar usuário fechava por acidente.
  // [[feedback-modal-no-click-outside-close]]
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
    >
      <div
        class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Icon width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({
  onCancel,
  onSubmit,
  submitting,
  valid,
  submitIcon: SubmitIcon,
  submitLabel
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  valid: boolean;
  submitIcon: typeof Save;
  submitLabel: string;
}) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!valid || submitting}
        class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
      >
        {submitting ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <SubmitIcon width="1em" height="1em" />
        )}
        {submitLabel}
      </button>
    </div>
  );
}
